import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { getPrescriptionViewDataForNewScript, transformAddress } from './prescriptionViewData'
import { GeneralError, PrescribedItem } from '../../../types'
import { FC, useEffect, useMemo, useState } from 'react'
import GoogleAnalyticsTagManager from '../../../utils/google-analytics/GoogleAnalyticsTagManager'
import { useGAContextData } from '../../../utils/google-analytics/useGAContextData'
import { rtpmPrecheck, RTPMResult } from './services/rtpmPrecheckService'
import { useAppSelector } from '../../../app/typedReduxHooks'
import { useFeatureToggle } from '../../../common/utils/feature-toggle/useFeatureToggle'
import { RTPMNotification } from './RTPMNotification'
import { ChevronDownIcon, ChevronUpIcon, WarningIcon } from '@chakra-ui/icons'
import { numberToWords } from '../utilities'
import { getLabelOfAuthority } from '../PrescribedItemList/utils/getLabelOfAuthority'
import { usePrescribingLocation } from '../../../features/locations/hooks/usePrescribingLocation'
import { analyticsAction } from '../../../common/utils/analytics'

interface DeliveryMethodProps {
  emergencySupply: boolean
  sendToPharmacy: boolean
  sendViaSMS: boolean
  printPaperToken: boolean
  sendViaEmail: boolean
  isPaperPrescription: boolean
  recipientEmail?: string
  recipientMobile?: string
}

const DeliveryMethod: FC<DeliveryMethodProps> = (props) => {
  const Content: FC<DeliveryMethodProps> = ({
    sendToPharmacy,
    emergencySupply,
    sendViaEmail,
    sendViaSMS,
    printPaperToken,
    isPaperPrescription,
    recipientEmail,
    recipientMobile,
  }) => {
    if (isPaperPrescription) {
      return <Text>Paper Prescription</Text>
    }
    if (sendToPharmacy || emergencySupply || printPaperToken) {
      return <Text>Paper Token</Text>
    }
    if (sendViaEmail) {
      return <Text>Email: {recipientEmail}</Text>
    }
    if (sendViaSMS) {
      return <Text>SMS: {recipientMobile}</Text>
    }
    return <Text>N/A</Text>
  }

  return (
    <Card p={4}>
      <Text fontWeight="bold" textTransform="uppercase">
        Script Delivery Method:
      </Text>
      <Content {...props} />
    </Card>
  )
}

interface ReviewPrescriptionModalProps {
  isOpen: boolean
  isPaperPrescription: boolean
  isIncludeS8: boolean
  onClose: () => void
  sendPrescription: () => void
}

const PreviewPrescriptionModal: FC<ReviewPrescriptionModalProps> = ({
  isOpen,
  onClose,
  sendPrescription,
  isPaperPrescription,
  isIncludeS8,
}) => {
  const { prescribingLocation } = usePrescribingLocation()
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const organization = useAppSelector((state) => state.organization?.data)
  const organizationSettings = organization?.settings
  const { isFeatureEnabled: isRTPMEnabled } = useFeatureToggle('RTPM')
  const GAContextData = useGAContextData()
  const prescribedItemList = useAppSelector((state) => state.newScript.prescribedItemList)
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)

  const data = getPrescriptionViewDataForNewScript(prescribingLocation)
  const drugs = useMemo(
    () => prescribedItemList.filter((item) => item !== undefined) as PrescribedItem[],
    [prescribedItemList]
  )
  const emptyDrugs = useMemo(() => prescribedItemList.filter((drug) => !drug), [prescribedItemList])

  const isCustomizedDrugs = useMemo(() => drugs.every((drug) => !drug.productDetails?.amt), [drugs])

  const enableRTPMFeature =
    isRTPMEnabled &&
    organizationSettings?.enableRTPM &&
    (organizationSettings?.forceEnableRTPM || prescriber?.enableRtpm) &&
    !isCustomizedDrugs

  const enableRTPMUIRestriction = enableRTPMFeature && organizationSettings?.enableRTPMUIRestriction
  const [rtpmResult, setRtpmResult] = useState<{ data?: RTPMResult; error?: GeneralError } | null>(
    null
  )
  const [rtpmCheckStatus, setRtpmCheckStatus] = useState<{
    checkRequired: boolean
    rtpmCheckLink: string
    dismissRequired: boolean
  } | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setRtpmResult(null)
      return
    }

    if (
      !prescribingLocation ||
      !prescriber ||
      !selectedPatient ||
      drugs.length === 0 ||
      isCustomizedDrugs
    ) {
      setRtpmResult(null)
      return
    }

    if (!enableRTPMFeature) {
      return
    }

    let cancelled = false

    const runRtpmPrecheck = async () => {
      const { data, error } = await rtpmPrecheck({
        patient: selectedPatient,
        clinic: prescribingLocation,
        prescriber,
        prescribedItems: drugs,
      })
      if (cancelled) return

      if (data) {
        setRtpmResult({
          data,
          error: undefined,
        })
        if (enableRTPMUIRestriction) {
          setRtpmCheckStatus({
            checkRequired: [1, 2].includes(data.code),
            rtpmCheckLink: data.contextualLinkUrl,
            dismissRequired: false,
          })
        }
      } else {
        setRtpmResult({
          data: undefined,
          error,
        })
        if (enableRTPMUIRestriction) {
          setRtpmCheckStatus({
            checkRequired: false,
            rtpmCheckLink: '',
            dismissRequired: true,
          })
        }
      }
    }

    runRtpmPrecheck()

    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    prescribingLocation,
    prescriber,
    selectedPatient,
    drugs,
    isCustomizedDrugs,
    enableRTPMFeature,
    enableRTPMUIRestriction,
  ])

  const onSend = () => {
    onClose()
    GoogleAnalyticsTagManager.sendCreatePrescriptionEvent(GAContextData)
    sendPrescription()
  }

  return (
    <Modal
      isOpen={isOpen}
      size={'xl'}
      onClose={onClose}
      blockScrollOnMount={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          Review & Submit Prescription
          <Text fontSize="md" mt={1} fontWeight="normal" color="gray.500">
            {`Confirm prescription details for ${drugs.length} ${
              drugs.length === 1 ? 'medication' : 'medications'
            }`}
          </Text>
        </ModalHeader>
        <ModalBody py={0}>
          <Divider my={2} />
          <Stack pt={4}>
            {enableRTPMFeature && (
              <RTPMNotification
                data={rtpmResult?.data}
                error={rtpmResult?.error}
                onCheck={() => {
                  setRtpmCheckStatus({
                    ...rtpmCheckStatus,
                    checkRequired: false,
                    dismissRequired: false,
                    rtpmCheckLink: '',
                  })
                }}
                onClose={() => {
                  if (rtpmCheckStatus?.dismissRequired) {
                    setRtpmCheckStatus({
                      ...rtpmCheckStatus,
                      dismissRequired: false,
                    })
                  }
                }}
              />
            )}

            <Grid display="grid" gap={4} gridTemplateColumns="1fr 1fr">
              <PrescriberInfoCard
                fullName={`${data.prescriberTitle} ${data.prescriberFirstname} ${data.prescriberLastname}`.trim()}
                hpii={data.prescriberHPII}
                address={`${data.clinicName}, ${transformAddress(
                  data.clinicAddress,
                  data.clinicAddress2
                )}, ${data.clinicSuburb} ${data.clinicState} ${data.clinicPostcode}`}
                phoneNumber={data.prescriberPhoneNumber || '-'}
                otherPhoneNumber={data.prescriberOtherPhoneNumber || '-'}
                email={data.prescriberEmail || '-'}
                providerNumber={data.prescriberProviderNumber || '-'}
                prescriberNumber={data.prescriberNumber || '-'}
              />
              <PatientInfoCard
                fullName={`${data.patientFirstname} ${data.patientLastname}`.trim()}
                dob={data.patientDob}
                medicareNumber={data.patientMedicareNumber}
                address={`${data.patientAddress}, ${data.patientSuburb} ${data.patientPostcode}`}
              />
            </Grid>

            <Divider my={4} />
            {drugs.map((item: PrescribedItem, index: number) => (
              <MedicationItem
                key={index}
                drug={item}
                serialNumber={index + 1}
                clinicState={data.clinicState}
              />
            ))}
            <DeliveryMethod
              isPaperPrescription={!!data.isPaperPrescription}
              sendToPharmacy={!!data.prescribedItems?.[0]?.sendToPharmacy}
              emergencySupply={!!data.prescribedItems?.[0]?.emergencySupply}
              sendViaEmail={!!data.sendViaEmail}
              sendViaSMS={!!data.sendViaSMS}
              printPaperToken={!!data.printPaperToken}
              recipientEmail={data.recipientEmail}
              recipientMobile={data.recipientMobile}
            />
          </Stack>

          {isPaperPrescription && isIncludeS8 ? (
            <Text color={'red.500'} fontWeight={'bold'} paddingTop={4}>
              Printed Schedule 8 prescriptions must comply with state or territory requirements
            </Text>
          ) : null}
          {emptyDrugs.length > 0 && (
            <Alert status="warning" my={4}>
              <AlertIcon as={WarningIcon} />
              <AlertDescription fontSize="sm">
                <Text>
                  {emptyDrugs.length}{' '}
                  {emptyDrugs.length === 1 ? 'empty prescription' : 'empty prescriptions'} will be
                  removed
                </Text>
              </AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter flexDirection="column" alignItems="stretch" gap={3}>
          <VStack align="stretch" width="100%" spacing={3}>
            {enableRTPMUIRestriction &&
            (rtpmCheckStatus?.checkRequired || rtpmCheckStatus?.dismissRequired) ? (
              <Alert status="error" width="100%">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {rtpmCheckStatus?.checkRequired ? (
                    <Text>
                      Active alert(s) exist. Click{' '}
                      <Link
                        href={rtpmCheckStatus?.rtpmCheckLink}
                        textDecoration="underline"
                        isExternal
                        onClick={() => {
                          setRtpmCheckStatus({
                            checkRequired: false,
                            dismissRequired: false,
                            rtpmCheckLink: '',
                          })
                        }}
                      >
                        HERE
                      </Link>{' '}
                      to view details in order to proceed with sending the prescription.
                    </Text>
                  ) : (
                    <Text>Please dismiss the above RTPM alert in order to proceed.</Text>
                  )}
                </AlertDescription>
              </Alert>
            ) : null}
            <HStack alignSelf={'end'}>
              <Button
                variant="ghost"
                mr={3}
                {...analyticsAction('review_make_changes')}
                onClick={() => {
                  setRtpmCheckStatus(null)
                  onClose()
                }}
              >
                Make Changes
              </Button>
              <Button
                colorScheme="blue"
                onClick={onSend}
                {...analyticsAction('review_send')}
                disabled={
                  enableRTPMUIRestriction &&
                  !(
                    rtpmCheckStatus?.checkRequired === false &&
                    rtpmCheckStatus?.dismissRequired === false
                  )
                }
              >
                Send
              </Button>
            </HStack>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const ChevronButton: FC<{
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}> = ({ isOpen, setIsOpen }) => {
  return (
    <IconButton
      variant="ghost"
      size="sm"
      fontSize="2xl"
      aria-label="Toggle Details"
      icon={isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
      onClick={() => setIsOpen(!isOpen)}
      _hover={{ backgroundColor: 'transparent' }}
    />
  )
}

const TextItem: FC<{
  label: string
  value: string
}> = ({ label, value }) => {
  return (
    <Text fontSize="sm" overflowWrap="break-word" wordBreak="break-word">
      <Text as="span" color="gray.500">
        {`${label}: `}
      </Text>
      <Text as="span" color="gray.700">
        {value}
      </Text>
    </Text>
  )
}

const PrescriberInfoCard: FC<{
  fullName: string
  hpii: string
  address: string
  phoneNumber: string
  otherPhoneNumber: string
  email: string
  providerNumber: string
  prescriberNumber: string
}> = ({
  fullName,
  hpii,
  address,
  phoneNumber,
  otherPhoneNumber,
  email,
  providerNumber,
  prescriberNumber,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Card bgColor="blue.50" position="relative" height="fit-content">
      <CardHeader color="blue.500" fontWeight="bold" pb="2">
        <Flex alignItems="center" justifyContent="space-between">
          <Text>Prescriber</Text>
          <ChevronButton isOpen={isOpen} setIsOpen={setIsOpen} />
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <Text>{fullName}</Text>
        <TextItem label="HPI-I" value={hpii} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Collapse in={isOpen}>
          <TextItem label="Address" value={address} />
          <TextItem label="Phone" value={phoneNumber} />
          <TextItem label="Other Phone" value={otherPhoneNumber} />
          <TextItem label="Email" value={email} />
          <TextItem label="Provider Number" value={providerNumber} />
          <TextItem label="Prescriber Number" value={prescriberNumber} />
        </Collapse>
      </CardBody>
    </Card>
  )
}

const PatientInfoCard: FC<{
  fullName: string
  dob: string
  medicareNumber: string
  address: string
}> = ({ fullName, dob, medicareNumber, address }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Card bgColor="blue.50" height="fit-content">
      <CardHeader color="blue.500" fontWeight="bold" pb="2">
        <Flex alignItems="center" justifyContent="space-between">
          <Text>Patient</Text>
          <ChevronButton isOpen={isOpen} setIsOpen={setIsOpen} />
        </Flex>
      </CardHeader>

      <CardBody pt={0}>
        <Text>{fullName}</Text>
        <TextItem label="DOB" value={dob} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Collapse in={isOpen}>
          <TextItem label="Medicare" value={medicareNumber || '-'} />
          <TextItem label="Address" value={address || '-'} />
        </Collapse>
      </CardBody>
    </Card>
  )
}

export const MedicationItem = ({
  drug,
  serialNumber,
  clinicState,
}: {
  drug: PrescribedItem
  serialNumber: number
  clinicState?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Card>
      <CardBody p={0}>
        <Grid templateColumns=".5fr 3fr 1fr 1.5fr" alignItems="flex-start" gap={2} p={4}>
          <GridItem>
            <Text color="gray.500">{serialNumber}.</Text>
          </GridItem>
          <GridItem>
            <Text>{drug.displayName || drug.genericName}</Text>
            <Flex gap={5} alignItems="center" color="gray.500" mt={2}>
              <Text>
                Qty: {drug.quantity} ({numberToWords(drug.quantity)?.toUpperCase()})
              </Text>
              <Text>Repeats: {drug.numberOfRepeatsAuthorised}</Text>
            </Flex>
          </GridItem>
          <GridItem justifySelf="end">
            <Badge variant="solid" colorScheme="green" px={3} py={1} fontSize="md">
              {drug.isPBS ? 'PBS' : 'RPBS'}
            </Badge>
          </GridItem>
          <GridItem
            display="flex"
            alignItems="center"
            justifySelf="end"
            color="blue.500"
            onClick={() => setIsOpen(!isOpen)}
            cursor="pointer"
          >
            Details
            {isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
          </GridItem>
        </Grid>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Collapse in={isOpen}>
          <Grid templateColumns="1fr 1fr" gap={4} bgColor="gray.50" p={4}>
            {drug.routeAdministration && (
              <GridItem>
                <Text color="gray.500">RoA</Text>
                <Text>{drug.routeAdministration}</Text>
              </GridItem>
            )}
            {drug.privatePrescription && (
              <GridItem>
                <Text color="gray.500">Type</Text>
                <Text>private</Text>
              </GridItem>
            )}
            {drug.controlledSubstanceReference && (
              <GridItem>
                <Text color="gray.500">Type</Text>
                <Text>Section 100</Text>
              </GridItem>
            )}
            {drug.pbsCode && (
              <GridItem>
                <Text color="gray.500">PBS Code</Text>
                <Text>{drug.pbsCode}</Text>
              </GridItem>
            )}
            {drug.phoneApprovalAuthorityNumber && (
              <GridItem>
                <Text color="gray.500">PBS Approval Number</Text>
                <Text>{drug.phoneApprovalAuthorityNumber}</Text>
              </GridItem>
            )}
            {drug.controlledSubstanceReference &&
              drug.controlledSubstanceReference !== 'Section 100' && (
                <GridItem>
                  <Text color="gray.500">{getLabelOfAuthority(clinicState)}</Text>
                  <Text>{drug.controlledSubstanceReference}</Text>
                </GridItem>
              )}
            {drug.patientInstructions && (
              <GridItem>
                <Text color="gray.500">Directions</Text>
                <Text>{drug.patientInstructions}</Text>
              </GridItem>
            )}
            {drug.reasonForPrescribing && (
              <GridItem>
                <Text color="gray.500">Reason for Prescribing</Text>
                <Text>{drug.reasonForPrescribing}</Text>
              </GridItem>
            )}
            {drug.doctorsNotes && (
              <GridItem>
                <Text color="gray.500">Annotation</Text>
                <Text>{drug.doctorsNotes}</Text>
              </GridItem>
            )}
            {drug.treatment && (
              <GridItem>
                <Text color="gray.500">Treatment Type</Text>
                <Text>{drug.treatment}</Text>
              </GridItem>
            )}
            {drug.prescribedItemReserved04 && (
              <GridItem>
                <Text color="gray.500">Name of the pharmacy</Text>
                <Text>{drug.prescribedItemReserved04}</Text>
              </GridItem>
            )}
            {drug.ctgAnnotation && (
              <GridItem>
                <Text color="gray.500">CTG Annotation</Text>
                <Text>{drug.ctgAnnotation}</Text>
              </GridItem>
            )}

            {drug.unusualDoseFlag && (
              <GridItem>
                <Text>Unusual Dose</Text>
              </GridItem>
            )}
            {drug.brandSubstitutionNotAllowed && (
              <GridItem>
                <Text>Brand Substitution Not Allowed</Text>
              </GridItem>
            )}
            {drug.regulation24 && (
              <GridItem>
                <Text>Regulation 49 (previously Regulation 24)</Text>
              </GridItem>
            )}
            {drug.emergencySupply && (
              <GridItem>
                <Text>Emergency supply</Text>
              </GridItem>
            )}
            {drug.noMySL && (
              <GridItem>
                <Text>
                  Patient has exercised their choice to keep the information away from their ASL
                </Text>
              </GridItem>
            )}
            {drug.sendToPharmacy && (
              <GridItem>
                <Text>{drug.sendToPharmacy}</Text>
              </GridItem>
            )}
          </Grid>
        </Collapse>
      </CardBody>
    </Card>
  )
}

export default PreviewPrescriptionModal
